import React, { useRef, useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Grid, useTheme, useMediaQuery, TableContainer, Table, TableBody, TableCell, TableRow } from '@mui/material';
import BarChart from './BarChart';
import LineChart from './LineChart';
import ResultadosDetalhados from './ResultadosDetalhados';
import { Button } from './ui';
import SecaoConclusao from './SecaoConclusao';

const ResultsScreen = ({ resultData, handleBack }) => {
  const scrollContainerRef = useRef(null);
  const [showScrollText, setShowScrollText] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        
        // Hide text when user is close to the bottom (95% scrolled) or at the very end
        setShowScrollText(scrollPercentage < 0.95 && scrollTop + clientHeight < scrollHeight - 50);
      }
    };

    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      // For Safari, use both scroll and touchmove events
      if (isSafari) {
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        scrollContainer.addEventListener('touchmove', handleScroll, { passive: true });
        scrollContainer.addEventListener('touchend', handleScroll, { passive: true });
        
        return () => {
          scrollContainer.removeEventListener('scroll', handleScroll);
          scrollContainer.removeEventListener('touchmove', handleScroll);
          scrollContainer.removeEventListener('touchend', handleScroll);
        };
      } else {
        // For other browsers, use the original logic
        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
      }
    }
  }, []);

  return (
    <Box 
      ref={scrollContainerRef}
      sx={{ minHeight: '100vh', height: '100vh', overflowY: 'auto', scrollSnapType: 'y mandatory', position: 'relative' }}
    >
      {/* Fixed scroll indicator */}
      {showScrollText && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 20,
            backdropFilter: 'blur(10px)',
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            Deslize para baixo para ver mais resultados
          </Typography>
        </Box>
      )}

      {/* Primeira visualização - Gráfico de Custos */}
      <Box sx={{ minHeight: '100vh', scrollSnapAlign: 'start', pb: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h1" sx={{ 
            mt: 4,
            mb: 2,
            fontWeight: 600, 
            textAlign: 'center', 
            color: 'white',
            fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' }
          }}>
            Simulador Fee Fixo vs Modelo Tradicional
          </Typography>
          <Paper sx={{ width: '100%', backgroundColor: 'white', borderRadius: 12, overflow: 'hidden', padding: { xs: 3, md: 3 }, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', mb: 4 }}>
            <BarChart data={resultData.chartData} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#555' }}>
                0,6% a 1,5% ao ano no mercado tradicional.
                <br />
                Na Monefica: fee fixo, sem comissão.
              </Typography>
              <Box sx={{ py: 1.5, px: 3, backgroundColor: '#FDA506', borderRadius: 8, display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Typography variant="body1" sx={{ 
                  fontWeight: 700, 
                  color: '#000', 
                  fontSize: { xs: '0.8rem', md: '1rem' }
                }}>
                  ECONOMIA TOTAL: R${resultData.totalEconomy.toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Segunda visualização - Detalhes de Patrimônio */}
      {!isMobile ? (
        // Desktop: Uma seção com os dois cards lado a lado
        <Box sx={{ minHeight: '100vh', scrollSnapAlign: 'start', pb: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Container maxWidth="lg">
            <ResultadosDetalhados resultData={resultData} />
          </Container>
        </Box>
      ) : (
        // Mobile: Duas seções separadas
        <>
          {/* Segunda seção - Card do gráfico de linha */}
          <Box sx={{ minHeight: '100vh', scrollSnapAlign: 'start', pb: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Container maxWidth="lg">
              <Box sx={{ width: '100%' }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 12, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', backgroundColor: '#fff' }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#121C42', fontSize: '1.1rem' }}>
                        Patrimônio Líquido Acumulado em um Período de 10 anos
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 3, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 10, height: 10, backgroundColor: '#121C42', mr: 1, borderRadius: '50%' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#333' }}>
                            Modelo tradicional
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 10, height: 10, backgroundColor: '#FDA506', mr: 1, borderRadius: '50%' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#333' }}>
                            Modelo fee fixo
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ height: 280, width: '100%', border: '1px solid #333' }}>
                        <LineChart data={resultData.chartData} />
                      </Box>
                      <TableContainer component={Box} sx={{ mb: 2 }}>
                        <Table size="small" sx={{ minWidth: '100%', border: '1px solid #333', borderCollapse: 'separate' }}>
                          <TableBody>
                            <TableRow>
                              {resultData.chartData
                                .filter((_, i) => isMobile ? [0, 2, 4, 6, 9].includes(i) : true) // Mobile: anos 1, 3, 5, 7, 10
                                .map((item, i, filteredArray) => (
                                <TableCell key={`header-${i}`} align="center" sx={{ 
                                  fontSize: { xs: '0.7rem', md: '0.75rem' }, 
                                  p: { xs: 0.5, md: 0.7 }, 
                                  fontWeight: 500, 
                                  color: '#333', 
                                  borderBottom: '1px solid #e0e0e0', 
                                  borderRight: i < filteredArray.length - 1 ? '1px solid #e0e0e0' : 'none' 
                                }}>
                                  Ano {item.year}
                                </TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              {resultData.chartData
                                .filter((_, i) => isMobile ? [0, 2, 4, 6, 9].includes(i) : true)
                                .map((item, i, filteredArray) => (
                                <TableCell key={`fee-${i}`} align="center" sx={{ 
                                  fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                  p: { xs: 0.6, md: 0.8 }, 
                                  color: '#FDA506', 
                                  fontWeight: 'bold', 
                                  borderBottom: '1px solid #e0e0e0', 
                                  borderRight: i < filteredArray.length - 1 ? '1px solid #e0e0e0' : 'none' 
                                }}>
                                  {isMobile ? (item.monefica < 1000 ? item.monefica : `${Math.round(item.monefica/1000)}k`) : item.monefica.toLocaleString('pt-BR')}
                                </TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              {resultData.chartData
                                .filter((_, i) => isMobile ? [0, 2, 4, 6, 9].includes(i) : true)
                                .map((item, i, filteredArray) => (
                                <TableCell key={`trad-${i}`} align="center" sx={{ 
                                  fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                  p: { xs: 0.6, md: 0.8 }, 
                                  color: '#121C42', 
                                  fontWeight: 'bold', 
                                  borderRight: i < filteredArray.length - 1 ? '1px solid #e0e0e0' : 'none' 
                                }}>
                                  {isMobile ? (item.traditional < 1000 ? item.traditional : `${Math.round(item.traditional/1000)}k`) : item.traditional.toLocaleString('pt-BR')}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
                        O mercado tradicional, a cobrança varia de 0,6% a 1,5% ao ano sobre o patrimônio.
                        <br />
                        Na Monefica, atuamos com fee fixo, sem comissões.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>

          {/* Terceira seção - Card de resultados */}
          <Box sx={{ minHeight: '100vh', scrollSnapAlign: 'start', pb: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Container maxWidth="lg">
              <Box sx={{ mt: 6, width: '100%' }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, height: '100%', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', backgroundColor: '#fff' }}>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: '#121C42', mb: 1 }}>
                        Resultado
                      </Typography>
                      
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ color: '#666', mb: 0.5 }}>
                          Fee fixo
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#121C42', fontSize: '1.4rem' }}>
                          R${resultData.moneficaFee.toLocaleString('pt-BR')} por ano
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#666', mb: 0.5 }}>
                          Modelo tradicional
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#121C42', fontSize: '1.4rem' }}>
                          R${resultData.traditionalFee.toLocaleString('pt-BR')} por ano
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        background: 'linear-gradient(15deg, #FDA506 0%, #FFFFFF 50%, #FDA506 100%)', 
                        p: 2, 
                        borderRadius: 4, 
                        mb: 1,
                        textAlign: 'left'
                      }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 0.5, fontSize: '1.4rem' }}>
                          Economia de
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.4rem' }}>
                          R${resultData.yearlyEconomy.toLocaleString('pt-BR')} por ano
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ color: '#666', mb: 1.5 }}>
                          Projeção de impacto ao longo de <strong>10 anos</strong>:
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#121C42', fontSize: '1.3rem', lineHeight: 1.2 }}>
                          Você pode economizar
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#121C42', fontSize: '1.4rem' }}>
                          R${resultData.totalEconomy.toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', lineHeight: 1.4 }}>
                        Mercado tradicional: entre 0,6% e 1,5% ao ano em comissões.
                        <br />
                        Na Monefica: fee fixo, sem comissão e sem conflito de interesse.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>
        </>
      )}

      {/* Última visualização - Conclusão */}
      <Box sx={{ 
        minHeight: '100vh', 
        scrollSnapAlign: 'start', 
        pb: 5, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60%',
          background: 'linear-gradient(to top, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 1
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <SecaoConclusao handleBack={handleBack} />
        </Container>
      </Box>
    </Box>
  );
};

export default ResultsScreen; 